import { Decoration } from '@atlaskit/editor-prosemirror/view';

import { getAllDiffObjects } from '../utils';

import { type DecorationTransformerParams, SpellingAndGrammarDecorations } from './index';

export const createDecoration = (from: number, to: number, id: string) =>
	Decoration.inline(
		from,
		to,
		{
			nodeName: 'span',
			class: `ai-suggestions`,
			id: `ai-sg-${id}`,
			'data-testid': `ai-suggestions`,
		},
		{
			key: `${SpellingAndGrammarDecorations.SPELLING_ERROR}_${from}_${to}`,
			chunkId: id,
		},
	);

export const updateSpellingDecoration = ({
	decorationSet,
	tr,
	pluginState,
}: DecorationTransformerParams) => {
	const allDiffObjects = getAllDiffObjects(pluginState);
	const allDiffObjectsIds = allDiffObjects.map((diffObject) => diffObject.id);
	const existingDecorationIds: string[] = [];

	// First step: remove decorations for diffObjects that does not exist any more.
	// For performance reasons, we will build existingDecorationIds here as well.
	const decorationsToRemove = decorationSet.find(undefined, undefined, (spec) => {
		existingDecorationIds.push(spec.chunkId);
		return !allDiffObjectsIds.includes(spec.chunkId);
	});
	decorationSet = decorationSet.remove(decorationsToRemove);

	// Second step: map existing decorations set with tr.mapping
	decorationSet = decorationSet.map(tr.mapping, tr.doc);

	// Third step: create new decorations for new diffObjects.
	const decorations = allDiffObjects?.reduce<Decoration[]>(
		(acc, { id, from, to, inlineNodePositions, originalText }) => {
			if (!existingDecorationIds.includes(id)) {
				// This will generate a decoration for each section of text, and if inline nodes exist then we will generate
				// deocrations around them using the pos/size in the inlineNodePositions to generate multiple decorations for each diff object
				// as a bare minimum we will always have a single decoration underline per diff object. If that diff contains 1 inline node
				// then we will have 2 decorations. In otherwords; we have (inlineNodePositions.length + 1) decorations
				let start = from;
				let end = inlineNodePositions.length > 0 ? from + inlineNodePositions[0].pos : to;
				acc.push(createDecoration(start, end, id));

				const n = inlineNodePositions.length;
				for (let i = 0; i < n; i++) {
					const { pos, size } = inlineNodePositions[i];
					start = from + pos + size;
					end = i < n - 1 ? from + inlineNodePositions[i + 1].pos : to;
					acc.push(createDecoration(start, end, id));
				}
			}
			return acc;
		},
		[],
	);

	// Fourth Step: If there are new decorations then add it in decorationsSet
	if (!!decorations?.length) {
		return decorationSet.add(tr.doc, decorations);
	}

	return decorationSet;
};
